import React, { useMemo, useState } from "react";

// OptionStrat-style matrix heatmap. Rows = stock-price slices, columns =
// snapshot dates (T+0 → expiry). Cells are color-shaded red→white→green
// by P&L (centered on $0). Hover shows the precise P&L; clicking a cell
// drives the scenario sliders so the chart morphs to that exact point.
//
// Source: `tn_curves` from /api/forge/build — a 9-snapshot × ~81-price grid
// already computed via Black-Scholes on the backend.
//
// Props:
//   tnCurves: [{days_forward, frac, curve:[{price,pnl}]}]
//   spot:     current underlying — used to pre-select ATM row
//   rowCount: how many price rows to keep (default 13, odd → ATM centered)
//   colCount: optional clamp on cols (default = all snapshots)
//   maxAbs:   color saturation point ($). null → derived from data.
//   onCellClick({frac, price, shockPct}) — used to drive the scenario sliders.

const lerp = (a, b, t) => a + (b - a) * t;

// Pick `count` rows from a sorted price list, centered on `spot`. Returns
// the actual price values (sampled from the curve so values match the
// backend's grid exactly).
const pickRows = (prices, spot, count) => {
  if (!prices.length) return [];
  const sorted = [...prices].sort((a, b) => a - b);
  if (sorted.length <= count) return sorted;
  // Find ATM index
  const atm = sorted.reduce((bestIdx, p, i) =>
    Math.abs(p - spot) < Math.abs(sorted[bestIdx] - spot) ? i : bestIdx, 0);
  // Stride so that we sample count points across the visible window
  const half = Math.floor(count / 2);
  const stride = Math.max(1, Math.floor(sorted.length / count));
  const out = [];
  // walk outward from ATM in both directions
  for (let i = -half; i <= half; i++) {
    const idx = Math.min(sorted.length - 1, Math.max(0, atm + i * stride));
    out.push(sorted[idx]);
  }
  // dedupe while keeping order
  return Array.from(new Set(out));
};

// Build a {price → pnl} map for each snapshot so cell lookup is O(1)
const indexSnapshot = (snap) => {
  const map = new Map();
  (snap.curve || []).forEach((pt) => map.set(+pt.price, +pt.pnl));
  return map;
};

const fmt$ = (v) => {
  if (!Number.isFinite(v)) return "—";
  const sign = v < 0 ? "−" : v > 0 ? "+" : "";
  const abs = Math.abs(v);
  if (abs >= 1000) return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `${sign}$${abs.toFixed(0)}`;
};

const monthDay = (daysForward) => {
  const d = new Date();
  d.setDate(d.getDate() + Math.round(daysForward || 0));
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const MatrixHeatmap = ({
  tnCurves = [],
  spot,
  rowCount = 13,
  colCount = null,
  maxAbs = null,
  onCellClick = null,
}) => {
  const snapshots = useMemo(
    () => (colCount ? tnCurves.slice(0, colCount) : tnCurves),
    [tnCurves, colCount],
  );

  const snapMaps = useMemo(() => snapshots.map(indexSnapshot), [snapshots]);

  const allPrices = useMemo(() => {
    if (!snapshots.length) return [];
    return (snapshots[0].curve || []).map((p) => +p.price);
  }, [snapshots]);

  const rows = useMemo(
    () => pickRows(allPrices, spot ?? allPrices[Math.floor(allPrices.length / 2)], rowCount),
    [allPrices, spot, rowCount],
  );

  // Auto-derive saturation point from data if not provided. Use the 95th
  // percentile of |pnl| so a single outlier doesn't wash out the heat scale.
  const autoMaxAbs = useMemo(() => {
    if (maxAbs != null) return maxAbs;
    const all = [];
    snapMaps.forEach((m) => m.forEach((v) => all.push(Math.abs(v))));
    if (!all.length) return 1;
    all.sort((a, b) => a - b);
    const p95 = all[Math.floor(all.length * 0.95)];
    return Math.max(p95, 1);
  }, [snapMaps, maxAbs]);

  const [hoverCell, setHoverCell] = useState(null);

  if (!snapshots.length || !rows.length) {
    return (
      <div
        className="bg-[#0E1A30] border border-white/10 rounded-xl p-6 text-center text-white/45 text-[12px]"
        data-testid="forge-matrix-empty"
      >
        Add at least one leg to compute the matrix.
      </div>
    );
  }

  // Reverse rows so the highest stock price renders at the top (matches
  // standard heatmap convention).
  const displayRows = [...rows].reverse();

  // Color: red for negative, green for positive, dark navy for ~zero
  const cellColor = (pnl) => {
    if (!Number.isFinite(pnl)) return "#0B1426";
    const t = Math.max(-1, Math.min(1, pnl / autoMaxAbs));
    if (t >= 0) {
      // 0..1 navy → bright green
      const r = Math.round(lerp(14, 16, t));
      const g = Math.round(lerp(26, 185, t));
      const b = Math.round(lerp(48, 95, t));
      return `rgb(${r}, ${g}, ${b})`;
    }
    const a = -t; // 0..1
    const r = Math.round(lerp(14, 239, a));
    const g = Math.round(lerp(26, 68, a));
    const b = Math.round(lerp(48, 68, a));
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleCellClick = (snap, price) => {
    if (!onCellClick) return;
    const shockPct = Number.isFinite(spot) && spot > 0 ? (price - spot) / spot : 0;
    onCellClick({ frac: snap.frac, days_forward: snap.days_forward, price, shockPct });
  };

  return (
    <div
      className="bg-[#0E1A30] border border-white/10 rounded-xl"
      data-testid="forge-matrix-heatmap"
    >
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/60">
          P&amp;L Matrix · <span className="text-white">Date × Stock Price</span>
        </div>
        <div className="ml-auto inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/40">
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm" style={{ background: "rgb(239,68,68)" }} />
            Loss
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm" style={{ background: "rgb(14,26,48)" }} />
            ≈0
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm" style={{ background: "rgb(16,185,95)" }} />
            Profit
          </span>
        </div>
      </div>

      <div className="overflow-auto max-h-[480px]">
        <table className="w-full border-collapse text-[11px]" data-testid="forge-matrix-table">
          <thead className="sticky top-0 bg-[#0E1A30] z-10 border-b border-white/10">
            <tr>
              <th className="text-[9.5px] uppercase tracking-[0.22em] text-white/45 font-medium px-3 py-2 text-right">
                Price ↓ / Date →
              </th>
              {snapshots.map((s, i) => (
                <th
                  key={`hdr-${i}`}
                  className="text-[10px] num text-white/55 font-medium px-1 py-2 text-center min-w-[58px]"
                  title={`T+${Math.round(s.days_forward)}d (${(s.frac * 100).toFixed(0)}% to expiry)`}
                >
                  <div className="num">{monthDay(s.days_forward)}</div>
                  <div className="text-[9px] text-white/35">T+{Math.round(s.days_forward)}d</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((price) => {
              const isATM = Number.isFinite(spot) && Math.abs(price - spot) < (allPrices[1] - allPrices[0]) * 1.5;
              return (
                <tr key={`row-${price}`}>
                  <td
                    className={`num font-medium px-3 py-1 text-right whitespace-nowrap ${
                      isATM ? "text-[#60A5FA] bg-[#3B82F6]/[0.06]" : "text-white/75"
                    }`}
                  >
                    ${price.toFixed(price % 1 === 0 ? 0 : 1)}
                  </td>
                  {snapshots.map((s, i) => {
                    const pnl = snapMaps[i].get(price);
                    const isHover = hoverCell && hoverCell.row === price && hoverCell.col === i;
                    return (
                      <td
                        key={`${price}-${i}`}
                        onMouseEnter={() => setHoverCell({ row: price, col: i, pnl })}
                        onMouseLeave={() => setHoverCell(null)}
                        onClick={() => handleCellClick(s, price)}
                        data-testid={`forge-matrix-cell-${i}-${price}`}
                        className={`text-center num text-[10px] py-1 cursor-pointer transition relative ${
                          isHover ? "ring-1 ring-white/60 z-10" : ""
                        }`}
                        style={{
                          background: cellColor(pnl),
                          color: Math.abs((pnl || 0) / autoMaxAbs) > 0.4 ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.7)",
                        }}
                      >
                        {fmt$(pnl)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 flex items-center justify-between flex-wrap gap-2">
        <span>Click any cell to set the scenario sliders to that date + price.</span>
        {hoverCell && Number.isFinite(hoverCell.pnl) && (
          <span className="num">
            ${hoverCell.row.toFixed(hoverCell.row % 1 === 0 ? 0 : 1)} @ T+{Math.round(snapshots[hoverCell.col]?.days_forward)}d ={" "}
            <b className={hoverCell.pnl >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}>
              {fmt$(hoverCell.pnl)}
            </b>
          </span>
        )}
      </div>
    </div>
  );
};

export default MatrixHeatmap;
